import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  getPhoenixApiTenantUrl,
  modify,
  get,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type {
  ConnectMangopayAddress,
  ConnectMangopayBankAccount,
  ConnectMangopayLegalRepresentative,
  ConnectMangopayServiceProvider,
} from '../types/Mangopay';
import type {
  RecipientSchemaParams,
  RecipientSchemaResponse,
  ValidateRecipientDataRequest,
  CreateRecipientRequest,
  RecipientCreationResponse,
  PayoutMethodsResponse,
  Recipient,
} from '../types/Recipient';

type ConnectMangopayServiceProviderWithFlatAddress = Omit<
  ConnectMangopayServiceProvider,
  'headquarters_address'
> &
  ConnectMangopayAddress;

export interface ConnectMangopayAccountBody
  extends ConnectMangopayServiceProviderWithFlatAddress {
  legal_representative_base_data: ConnectMangopayLegalRepresentative;
  return_url?: string;
}

export interface IOnboardingPhoenixApi extends Api {
  acceptInvitation(dataIn: {
    params: {
      nursing_home_id: string;
    };
  }): Promise<void>;

  connectMangopayAccount(dataIn: {
    body: {
      data: ConnectMangopayAccountBody;
    };
  }): Promise<{
    status: 'OK';
    redirect_url?: string;
  }>;

  connectMangopayBankAccount(dataIn: {
    body: {
      data: ConnectMangopayBankAccount;
    };
  }): Promise<{
    status: 'OK';
  }>;

  enrollScaEnrollment(dataIn: {
    body: {
      return_url: string;
    };
  }): Promise<{
    redirect_url: string | null;
  }>;

  getPayoutMethods(dataIn: {
    params: { currency: string };
  }): Promise<PayoutMethodsResponse>;

  getRecipientSchema(dataIn: {
    params: RecipientSchemaParams;
  }): Promise<RecipientSchemaResponse>;

  validateRecipientData(dataIn: {
    body: ValidateRecipientDataRequest;
  }): Promise<{ data: { valid: boolean; errors?: string[] } }>;

  createRecipient(dataIn: {
    body: CreateRecipientRequest;
  }): Promise<RecipientCreationResponse>;

  getRecipient(): Promise<{
    service_provider: {
      global_service_provider_id: string;
      name: string;
      mangopay_bank_account_id: string | null;
      mangopay_recipient_status:
        | 'PENDING'
        | 'ACTIVE'
        | 'CANCELED'
        | 'DEACTIVATED'
        | null;
      iban_last_4: string | null;
    };
    recipient: Recipient | null;
  }>;
}

export class OnboardingPhoenixApi implements IOnboardingPhoenixApi {
  headers: Headers;

  acceptInvitation: IOnboardingPhoenixApi['acceptInvitation'];
  connectMangopayAccount: IOnboardingPhoenixApi['connectMangopayAccount'];
  connectMangopayBankAccount: IOnboardingPhoenixApi['connectMangopayBankAccount'];
  enrollScaEnrollment: IOnboardingPhoenixApi['enrollScaEnrollment'];
  getPayoutMethods: IOnboardingPhoenixApi['getPayoutMethods'];
  getRecipientSchema: IOnboardingPhoenixApi['getRecipientSchema'];
  validateRecipientData: IOnboardingPhoenixApi['validateRecipientData'];
  createRecipient: IOnboardingPhoenixApi['createRecipient'];
  getRecipient: IOnboardingPhoenixApi['getRecipient'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    const baseUrl = getPhoenixApiTenantUrl('service-provider', apiUrl);
    this.headers = new Headers({
      'content-type': 'application/json',
      'x-pn-stripemode': 'live',
    });

    this.acceptInvitation = modify({
      authContext,
      method: 'POST',
      url: (params) =>
        `${baseUrl}/nursing-home/${params.nursing_home_id}/accept`,
    });

    this.connectMangopayBankAccount = modify({
      authContext,
      headers: this.headers,
      url: `${baseUrl}/profile/bank-account`,
      method: 'POST',
    });

    this.connectMangopayAccount = modify({
      authContext,
      headers: this.headers,
      url: `${baseUrl}/profile`,
      method: 'PUT',
    });

    this.enrollScaEnrollment = modify({
      authContext,
      headers: this.headers,
      url: `${baseUrl}/profile/mangopay-sca-enrollment`,
      method: 'POST',
    });

    this.getPayoutMethods = get({
      authContext,
      headers: this.headers,
      url: (params: { currency: string }) =>
        `${baseUrl}/profile/recipient/payout-methods?currency=${params.currency}`,
    });

    this.getRecipientSchema = get({
      authContext,
      headers: this.headers,
      url: (params: RecipientSchemaParams) => {
        let url = `${baseUrl}/profile/recipient/schema?currency=${params.currency}`;
        if (params.payout_method_type) {
          url += `&payout_method_type=${params.payout_method_type}`;
        }
        if (params.country) {
          url += `&country=${params.country}`;
        }
        return url;
      },
    });

    this.validateRecipientData = modify({
      authContext,
      headers: this.headers,
      url: `${baseUrl}/profile/recipient/validate`,
      method: 'POST',
    });

    this.createRecipient = modify({
      authContext,
      headers: this.headers,
      url: `${baseUrl}/profile/recipient`,
      method: 'POST',
    });

    this.getRecipient = get({
      authContext,
      headers: this.headers,
      url: `${baseUrl}/profile/recipient`,
    });
  }
}
